import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, MessageSquare, ThumbsUp, ThumbsDown, User, 
  Plus, Send, LogIn, Award, Clock,
  Tag, Search, Settings, LogOut
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommunityPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, isLoggedIn, setShowLoginModal, requireLogin, logout } = useAuth();
  
  const [isLoading, setIsLoading] = useState(false);
  const [threads, setThreads] = useState([]);
  const [showCreateThread, setShowCreateThread] = useState(false);
  const [newThread, setNewThread] = useState({ title: '', body: '', tags: '' });
  const [selectedThread, setSelectedThread] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Pre-fill venue info if coming from report page
  const prefilledVenue = location.state?.venue_name || null;
  const prefilledVenueId = location.state?.venue_id || null;

  useEffect(() => {
    fetchThreads();
  }, []);

  const fetchThreads = async () => {
    try {
      const response = await axios.get(`${API_URL}/community/threads`);
      setThreads(response.data);
    } catch (error) {
      console.error('Failed to fetch threads');
    }
  };

  const fetchThreadDetails = async (threadId) => {
    try {
      const response = await axios.get(`${API_URL}/community/threads/${threadId}`);
      setSelectedThread(response.data.thread);
      setComments(response.data.comments);
    } catch (error) {
      toast.error('Failed to load discussion');
    }
  };

  const handleCreateThread = async () => {
    if (!isLoggedIn) {
      requireLogin('create a discussion', () => setShowCreateThread(true));
      return;
    }
    
    if (!newThread.title.trim() || !newThread.body.trim()) {
      toast.error('Please fill in title and description');
      return;
    }
    
    setIsLoading(true);
    try {
      const tags = newThread.tags.split(',').map(t => t.trim()).filter(t => t);
      const payload = {
        title: newThread.title,
        body: newThread.body,
        tags,
        venue_id: prefilledVenueId,
        venue_name: prefilledVenue
      };
      
      const response = await axios.post(`${API_URL}/community/threads?token=${token}`, payload);
      setThreads(prev => [response.data, ...prev]);
      setNewThread({ title: '', body: '', tags: '' });
      setShowCreateThread(false);
      toast.success('Discussion created!');
    } catch (error) {
      if (error.response?.status === 401) {
        setShowLoginModal(true);
      } else {
        toast.error('Failed to create discussion');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePostComment = async () => {
    if (!isLoggedIn) {
      requireLogin('post a comment', () => {});
      return;
    }
    
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/community/comments?token=${token}`, {
        thread_id: selectedThread.id,
        body: newComment
      });
      setComments(prev => [...prev, response.data]);
      setNewComment('');
      toast.success('Comment posted!');
    } catch (error) {
      toast.error('Failed to post comment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVote = async (itemType, itemId, voteType) => {
    if (!isLoggedIn) {
      requireLogin('vote', () => {});
      return;
    }
    
    try {
      await axios.post(`${API_URL}/community/vote?item_type=${itemType}&item_id=${itemId}&vote_type=${voteType}&token=${token}`);
      
      if (itemType === 'thread') {
        setThreads(prev => prev.map(t => 
          t.id === itemId ? { ...t, [voteType === 'up' ? 'upvotes' : 'downvotes']: t[voteType === 'up' ? 'upvotes' : 'downvotes'] + 1 } : t
        ));
      } else {
        setComments(prev => prev.map(c => 
          c.id === itemId ? { ...c, [voteType === 'up' ? 'upvotes' : 'downvotes']: c[voteType === 'up' ? 'upvotes' : 'downvotes'] + 1 } : c
        ));
      }
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const handleStartDiscussion = () => {
    if (!isLoggedIn) {
      requireLogin('start a discussion', () => setShowCreateThread(true));
      return;
    }
    setShowCreateThread(true);
  };

  const filteredThreads = threads.filter(t => 
    t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.body.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFCF8]/95 backdrop-blur-sm border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-serif text-xl font-medium text-stone-900">WVC Community</h1>
              <p className="text-sm text-stone-500">Discuss venues with real couples</p>
            </div>
          </div>
          
          {user ? (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-2 bg-stone-100 rounded-full">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                ) : (
                  <User className="w-5 h-5 text-stone-600" />
                )}
                <span className="text-sm font-medium text-stone-700">{user.name}</span>
                <div className="flex items-center gap-1 text-amber-600">
                  <Award className="w-4 h-4" />
                  <span className="text-xs font-medium">{user.reputation || 0}</span>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          ) : (
            <Button onClick={() => setShowLoginModal(true)} className="bg-stone-900 hover:bg-stone-800" data-testid="login-btn">
              <LogIn className="w-4 h-4 mr-2" />
              Login
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Search & Create */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
            <Input 
              placeholder="Search discussions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white border-stone-200"
              data-testid="search-discussions"
            />
          </div>
          <Button 
            onClick={handleStartDiscussion}
            className="bg-stone-900 hover:bg-stone-800"
            data-testid="create-thread-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Start Discussion
          </Button>
        </div>

        {/* Threads List */}
        <div className="space-y-4">
          {filteredThreads.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
              <MessageSquare className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-stone-700 mb-2">No discussions yet</h3>
              <p className="text-stone-500 mb-4">Be the first to start a conversation!</p>
              <Button onClick={handleStartDiscussion} className="bg-stone-900 hover:bg-stone-800">
                Start Discussion
              </Button>
            </div>
          ) : (
            filteredThreads.map(thread => (
              <div 
                key={thread.id}
                className="bg-white rounded-xl border border-stone-200 p-5 hover:border-stone-300 transition-colors cursor-pointer"
                onClick={() => fetchThreadDetails(thread.id)}
                data-testid={`thread-${thread.id}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-medium text-stone-900 hover:text-stone-700">{thread.title}</h3>
                  <div className="flex items-center gap-2 text-sm text-stone-500">
                    <Clock className="w-4 h-4" />
                    {new Date(thread.created_at).toLocaleDateString()}
                  </div>
                </div>
                <p className="text-stone-600 mb-4 line-clamp-2">{thread.body}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1 text-stone-500">
                      <User className="w-4 h-4" />
                      <span className="text-sm">{thread.author_name}</span>
                    </div>
                    {thread.venue_name && (
                      <span className="text-sm text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                        {thread.venue_name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-stone-500">
                    <div className="flex items-center gap-1">
                      <ThumbsUp className="w-4 h-4" />
                      {thread.upvotes}
                    </div>
                    <div className="flex items-center gap-1">
                      <MessageSquare className="w-4 h-4" />
                      {thread.comment_count}
                    </div>
                  </div>
                </div>
                {thread.tags?.length > 0 && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {thread.tags.map(tag => (
                      <span key={tag} className="text-xs bg-stone-100 text-stone-600 px-2 py-1 rounded-full flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </main>

      {/* Create Thread Modal */}
      <Dialog open={showCreateThread} onOpenChange={setShowCreateThread}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Start a Discussion</DialogTitle>
            <DialogDescription>Share your venue experience or ask questions</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <Input 
              placeholder="Discussion title"
              value={newThread.title}
              onChange={(e) => setNewThread(prev => ({ ...prev, title: e.target.value }))}
              data-testid="thread-title-input"
            />
            <Textarea 
              placeholder="Share your thoughts, questions, or experiences..."
              value={newThread.body}
              onChange={(e) => setNewThread(prev => ({ ...prev, body: e.target.value }))}
              rows={5}
              data-testid="thread-body-input"
            />
            <Input 
              placeholder="Tags (comma separated)"
              value={newThread.tags}
              onChange={(e) => setNewThread(prev => ({ ...prev, tags: e.target.value }))}
              data-testid="thread-tags-input"
            />
            {prefilledVenue && (
              <div className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                Discussing: {prefilledVenue}
              </div>
            )}
            <Button 
              onClick={handleCreateThread}
              className="w-full bg-stone-900 hover:bg-stone-800"
              disabled={isLoading}
              data-testid="submit-thread-btn"
            >
              {isLoading ? 'Posting...' : 'Post Discussion'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Thread Detail Modal */}
      <Dialog open={!!selectedThread} onOpenChange={() => setSelectedThread(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedThread && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">{selectedThread.title}</DialogTitle>
                <div className="flex items-center gap-4 text-sm text-stone-500 mt-2">
                  <span>{selectedThread.author_name}</span>
                  <span>{new Date(selectedThread.created_at).toLocaleDateString()}</span>
                </div>
              </DialogHeader>
              
              <div className="mt-4">
                <p className="text-stone-700 whitespace-pre-wrap">{selectedThread.body}</p>
                
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-stone-200">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleVote('thread', selectedThread.id, 'up')}
                    className="text-stone-600"
                    data-testid="upvote-thread-btn"
                  >
                    <ThumbsUp className="w-4 h-4 mr-1" />
                    {selectedThread.upvotes}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleVote('thread', selectedThread.id, 'down')}
                    className="text-stone-600"
                    data-testid="downvote-thread-btn"
                  >
                    <ThumbsDown className="w-4 h-4 mr-1" />
                    {selectedThread.downvotes}
                  </Button>
                </div>
                
                {/* Comments */}
                <div className="mt-6">
                  <h4 className="font-medium text-stone-900 mb-4">Comments ({comments.length})</h4>
                  
                  <div className="space-y-4 mb-6">
                    {comments.map(comment => (
                      <div key={comment.id} className="bg-stone-50 rounded-lg p-4" data-testid={`comment-${comment.id}`}>
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-medium text-stone-700">{comment.author_name}</span>
                          <span className="text-xs text-stone-500">{new Date(comment.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-stone-600">{comment.body}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <button 
                            onClick={() => handleVote('comment', comment.id, 'up')}
                            className="text-stone-500 hover:text-stone-700 flex items-center gap-1 text-sm"
                          >
                            <ThumbsUp className="w-3 h-3" />
                            {comment.upvotes}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add Comment */}
                  <div className="flex gap-2">
                    <Textarea 
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      rows={2}
                      className="flex-1"
                      data-testid="comment-input"
                    />
                    <Button 
                      onClick={handlePostComment}
                      className="bg-stone-900 hover:bg-stone-800"
                      disabled={isLoading}
                      data-testid="post-comment-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunityPage;
